import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import CloudStorage from "../../../../../CloudStorage/CloudStorage";
import { urtoast } from "../../../../Layouts/CustomPopup/eventEmitter";
import { v4 as uuidv4 } from "uuid";
import DisplayFileProper from "../../../../Common/FilesRelevent/DisplayFileProper";
import DownloadButton from "../../../../Common/FilesRelevent/DownloadButton";
import { validDocumentTypes, validImageTypes } from "../../../../../assests/extention/fileextention";
import AsteriskLabel from "../../../../Common/AsteriskLabel";

import useFileValidator from "../../../../../hooks/useFileValidator";
import UploadLoader from "../../../../Common/Loader/UploadLoader";

export default function IAddPortfolioImages({
  formik,
}) {
  const { validating, validateFiles } = useFileValidator();
  
  // Track the Firebase upload process
  const [isUploading, setIsUploading] = useState(false);

  // Use formik.values.files directly as the source of truth
  const selectedFiles = useMemo(() =>
    Array.isArray(formik.values.files) ? formik.values.files : []
    , [formik.values.files]);

  const handleUpload = async (files) => {
    // 🛡️ Validate all files via backend API
    const isValid = await validateFiles(files);
    if (!isValid) return;

    // Start loader before initiating uploads
    setIsUploading(true);

    const uploadPromises = files.map((file) => {
      if (file.size > 10 * 1024 * 1024) {
        urtoast(`File size exceeds 10 MB limit.`, 2);
        return Promise.reject(new Error("File size exceeds limit."));
      }

      const uniqueId1 = uuidv4();
      const storageRef = ref(
        getStorage(),
        `/Category/${uniqueId1}_${file.name}`,
      );
      
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return uploadTask
        .then(async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          return { file: url };
        })
        .catch((error) => {
          urtoast(`Error uploading ${file.name}: ${error.message}`);
          throw error;
        });
    });

    Promise.all(uploadPromises)
      .then((urls) => {
        const updatedFiles = [...selectedFiles, ...urls];
        formik.setFieldValue("files", updatedFiles);
      })
      .catch((error) => {
        console.error("Error uploading files: ", error);
      })
      .finally(() => {
        // Stop loader regardless of success or failure
        setIsUploading(false);
      });
  };

  const onDrop = (acceptedFiles) => {
    handleUpload(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip']
    }
  });

  const handleDeleteImage = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    formik.setFieldValue("files", updatedFiles);
  };

  return (
    <div className="add-portfolio position-relative">
      
      {/* 🚀 Render loader if either validating hook OR upload state is active */}
      {(validating || isUploading) && <UploadLoader />}
      
      <h6 className="skill-right file-attachment">
        Upload file <AsteriskLabel /> <span> (Upload at least 1 file)</span>
      </h6>
      <div className="col-md-5 p-0">
        {selectedFiles.length === 0 ? (
          <div className="image-container">
            <img
              src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/file-upload.png`}
              className="cover-img w-100"
              alt="Certificate"
            />
          </div>
        ) : (
          selectedFiles.map((item, index) => (
            <div key={index} className="image-container">
              <DisplayFileProper item={item} />
              <DownloadButton file={item} />
              <button
                type="button"
                className="delete-button"
                onClick={() => handleDeleteImage(index)}
              >
                <img
                  src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/delete-icon.svg`}
                  alt="Delete"
                />
              </button>
            </div>
          ))
        )}
      </div>
      {formik.touched.files && formik.errors.files ? (
        <p style={{ color: "red" }}>{formik.errors.files}</p>
      ) : null}
      
      {/* Optionally: Disable dropzone visually when uploading to prevent concurrent uploads */}
      <div 
        {...getRootProps({ 
          className: `dropzone borderstylex ${(validating || isUploading) ? 'opacity-50 pointer-events-none' : ''}` 
        })}
      >
        <input {...getInputProps()} disabled={validating || isUploading} />
        {isDragActive ? (
          <p>Drop the files here...</p>
        ) : (
          <p>
            Drag and drop or <span>Browse file</span>
          </p>
        )}
      </div>
      
      <ul className="border-ul">
        <li>Images (JPG, PNG, JPEG up to 10MB)</li>
        <li>Documents (PDF, DOCX, XLSX, PPTX, ZIP upto 10MB)</li>
      </ul>
    </div>
  );
}